// militaryDtg.js — sitrep payload-shape overhaul (MINSKY-16404 design review): date/time
// formatting for the "Generate Situation Report" payload.
//
// Existing-utility audit (per the review's instruction to reuse before writing a new one):
//   - reportMeta.helpers.js's `formatDtg` and documentHeaderExport.js's `formatHeaderDtgDisplay`
//     both format a date for a *document header field* display ("YYYY-MM-DD HH:mm[Z]") — useful
//     for a human reading the report editor UI, not the military "DDHHMMZ MON YY" zulu format a
//     sitrep's "as of" line needs verbatim, and neither emits a plain ISO-8601 string either.
//   - useDtgRange.js works with dayjs objects for the timeline UI's own bounds/scrub state; it
//     doesn't format a display string at all.
//   - No other DTG-formatting-to-string utility exists in the codebase (searched for DTG/Zulu/
//     militar*/formatDtg-family names) — hence this new, small, tested module.
//
// Both functions here are pure/synchronous and never throw — same "never throws" contract as
// sidcDecoder.js's decodeSidc, since this is also called over an entire marking/event set from
// buildSitrepPayload.js without a caller-side try/catch.

const MONTH_ABBREVIATIONS = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];

function pad2(n) {
    return String(n).padStart(2, '0');
}

/**
 * Epoch ms -> UTC ISO-8601 string, for every "machine date" field in the sitrep payload
 * (generatedIso, asOfIso, event timestamps, etc.) — see this module's doc comment for why ISO
 * replaces raw epoch-ms per the design review.
 *
 * @param {number} epochMs
 * @returns {string|null} `null` for a non-finite/missing input rather than throwing.
 */
export function epochMsToIso(epochMs) {
    if (!Number.isFinite(epochMs)) return null;
    return new Date(epochMs).toISOString();
}

/**
 * Epoch ms -> military DTG string, format `DDHHMMZ MON YY` (e.g. "140000Z JUL 26") — always UTC
 * ("Z"/Zulu time). This is the ONE pre-formatted date string the sitrep payload carries verbatim
 * for the report's "as of" date (see buildSitrepPayload.js's `report.asOfDtg`); every other date
 * in the payload is plain ISO-8601 (`epochMsToIso` above).
 *
 * @param {number} epochMs
 * @returns {string|null} `null` for a non-finite/missing input rather than throwing.
 */
export function epochMsToMilitaryDtg(epochMs) {
    if (!Number.isFinite(epochMs)) return null;
    const d = new Date(epochMs);
    const day = pad2(d.getUTCDate());
    const hour = pad2(d.getUTCHours());
    const minute = pad2(d.getUTCMinutes());
    const month = MONTH_ABBREVIATIONS[d.getUTCMonth()];
    const year = pad2(d.getUTCFullYear() % 100);
    return `${day}${hour}${minute}Z ${month} ${year}`;
}
